import type { ActiveCreditBureau, CreditReportApplicationData, CreditReportJobMeta, NormalizedCreditCollectionRow, NormalizedCreditPublicRecordRow } from '../types/utils'
/** Credit Report tab helpers (Angular creditinfo parity). */


const KNOWN_BUREAUS: ActiveCreditBureau[] = ["ZipReport", "CoreLogic", "Equifax"]

/** Bureau from `GET /api/config/cb_active` only — no applicant-data inference (Angular `configDetail?.active`). */
export function parseConfigCreditBureau(
  configActive?: string | null,
): ActiveCreditBureau {
  if (typeof configActive !== "string") return null
  return KNOWN_BUREAUS.find((bureau) => bureau === configActive) ?? null
}

/** Resubmit/Create buttons need config bureau + payment + policy (Angular). */
export function shouldShowResubmitCreditReportButton(input: {
  configActive?: string | null
  hasPayment?: boolean
  canResubmitCreditReport?: boolean
}): boolean {
  const bureau = parseConfigCreditBureau(input.configActive)
  return Boolean(
    input.hasPayment &&
      input.canResubmitCreditReport &&
      bureau &&
      (bureau === "ZipReport" ||
        bureau === "CoreLogic" ||
        bureau === "Equifax"),
  )
}

/** Pre-approve application statuses — Retry Income Report is allowed only here. */
export const RETRY_INCOME_REPORT_APPLICATION_STATUSES = new Set([
  "NOT_STARTED",
  "INCOMPLETE",
  "PENDING",
  "AWAITING",
])

/**
 * "Retry Income Report" visibility:
 * ROLE_ADMIN | ROLE_UNDERWRITER, application status lower than APPROVED,
 * has bank linking steps.
 */
export function shouldShowResubmitIncomeReportButton(input: {
  currentUserRole?: string | null
  applicationStatus?: string | null
  hasBankLinkingSteps?: boolean
}): boolean {
  const role = (input.currentUserRole || "").trim()
  if (role !== "ROLE_ADMIN" && role !== "ROLE_UNDERWRITER") return false
  const status = (input.applicationStatus || "").trim().toUpperCase()
  if (!RETRY_INCOME_REPORT_APPLICATION_STATUSES.has(status)) return false
  if (!input.hasBankLinkingSteps) return false
  return true
}


const isPresentScalar = (value: unknown): boolean => {
  if (value === null || value === undefined) return false
  if (value === 0 || value === "0" || value === "0.00") return true
  if (typeof value === "string") return value.trim() !== ""
  return Boolean(value)
}

/** Active bureau: config `active`, else infer from applicant data (`0`/`"0"` counts as present). */
export function resolveActiveCreditBureau(input: {
  configActive?: string | null
  applicationData?: CreditReportApplicationData | null
}): ActiveCreditBureau {
  const configActive = input.configActive
  if (typeof configActive === "string") {
    const match = KNOWN_BUREAUS.find((bureau) => bureau === configActive)
    if (match) return match
  }

  const data = input.applicationData
  if (!data) return null

  if (
    isPresentScalar(data.equifax_score) ||
    isPresentScalar(data.equifax_credit_report_path)
  ) {
    return "Equifax"
  }

  if (
    isPresentScalar(data.corelogic_credit_report_path) ||
    isPresentScalar(data.corelogic_applicant_score)
  ) {
    return "CoreLogic"
  }

  const credit = data.credit_report
  if (credit === "1" || credit === 1) return "ZipReport"

  return null
}

/** Iframe remount key when credit payload changes. */
export function creditReportHtmlIframeRevision(
  applicationData?: Record<string, unknown> | null,
): string {
  if (!applicationData || typeof applicationData !== "object") return "0"
  const d = applicationData
  const hist = d.credit_report_history
  let histSig = ""
  if (Array.isArray(hist) && hist.length > 0) {
    const first = hist[0] as Record<string, unknown>
    histSig = [first?.id, first?.created_at, first?.score]
      .map((v) => (v == null ? "" : String(v)))
      .join(":")
  }
  const crd = d.credit_report_data
  let dataSig = ""
  if (typeof crd === "string") {
    dataSig = `s:${crd.length}`
  } else if (crd != null && typeof crd === "object") {
    try {
      dataSig = `o:${JSON.stringify(crd).length}`
    } catch {
      dataSig = "o:?"
    }
  }
  return [
    d.updated_at,
    d.modified_at,
    d.equifax_score,
    d.credit_report_html_url,
    d.equifax_credit_report_path,
    histSig,
    dataSig,
  ]
    .map((v) => (v == null ? "" : String(v)))
    .join("|")
}

/** ZipReport resubmit confirm when `credit_report` is job meta object, not `"1"`/`"0"` flag. */
export function hasZipReportCreditReportMeta(
  applicationAllData?: Record<string, unknown> | null,
): boolean {
  const raw = applicationAllData?.credit_report
  if (raw == null) return false
  return typeof raw === "object" && !Array.isArray(raw)
}

function creditReportMetaObject(
  applicationData?: Record<string, unknown> | null,
  applicationAllData?: Record<string, unknown> | null,
): Record<string, unknown> | null {
  const pick = (raw: unknown): Record<string, unknown> | null => {
    if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return null
    const o = raw as Record<string, unknown>
    if (
      "queued_job" in o ||
      "failed_job" in o ||
      "has_credit_changed" in o
    ) {
      return o
    }
    return null
  }
  return (
    pick(applicationData?.credit_report) ??
    pick(applicationAllData?.credit_report) ??
    null
  )
}

export function extractCreditReportJobMeta(
  applicationData?: Record<string, unknown> | null,
  applicationAllData?: Record<string, unknown> | null,
): CreditReportJobMeta {
  const meta = creditReportMetaObject(applicationData, applicationAllData)
  const failedRaw = meta?.failed_job
  let failedJobId: string | null = null
  if (failedRaw != null && typeof failedRaw === "object" && !Array.isArray(failedRaw)) {
    const id = (failedRaw as { id?: unknown }).id
    if (id != null && String(id).trim() !== "") failedJobId = String(id)
  } else if (failedRaw != null && failedRaw !== "") {
    failedJobId = String(failedRaw)
  }

  return {
    hasQueuedJob: Boolean(meta?.queued_job),
    hasFailedJob: Boolean(failedRaw),
    hasCreditChanged: meta?.has_credit_changed as boolean | undefined,
    failedJobId,
  }
}

function asPlainObject(raw: unknown): Record<string, unknown> | null {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return null
  return raw as Record<string, unknown>
}

/** Parse `score_notes` JSON string or object (Angular Hit Code binding). */
export function parseScoreNotesRaw(raw: unknown): Record<string, unknown> {
  if (raw == null) return {}
  if (typeof raw === "string") {
    const t = raw.trim()
    if (!t) return {}
    try {
      const parsed = JSON.parse(t) as unknown
      return asPlainObject(parsed) ?? {}
    } catch {
      return {}
    }
  }
  return asPlainObject(raw) ?? {}
}

function pickScoreNoteString(
  obj: Record<string, unknown>,
  candidates: string[],
): string | null {
  for (const c of candidates) {
    if (!Object.prototype.hasOwnProperty.call(obj, c)) continue
    const v = obj[c]
    if (v === undefined || v === null) continue
    const s = String(v)
    if (s.trim() !== "") return s
  }
  const compactKeyMap = new Map<string, unknown>()
  for (const [k, v] of Object.entries(obj)) {
    compactKeyMap.set(k.toLowerCase().replace(/\s+/g, ""), v)
  }
  for (const c of candidates) {
    const key = c.toLowerCase().replace(/\s+/g, "")
    const v = compactKeyMap.get(key)
    if (v === undefined || v === null) continue
    const s = String(v)
    if (s.trim() !== "") return s
  }
  return null
}

/** Hit code for a history row or current score notes — preserves numeric `0` (Angular shows it). */
export function getHitCodeFromScoreNotes(scoreNotesRaw: unknown): string | null {
  const o = parseScoreNotesRaw(scoreNotesRaw)
  return pickScoreNoteString(o, [
    "Hit Code",
    "hit_code",
    "Hit code",
    "HIT_CODE",
    "hitCode",
  ])
}

export function getHitDescriptionFromScoreNotes(scoreNotesRaw: unknown): string | null {
  const o = parseScoreNotesRaw(scoreNotesRaw)
  return pickScoreNoteString(o, [
    "Hit Description",
    "hit_description",
    "Hit description",
    "HIT_DESCRIPTION",
    "hitDescription",
    "Hit and Automated Consumer Narrative",
    "hit_and_automated_consumer_narrative",
    "Consumer Narrative",
    "consumer_narrative",
  ])
}

export function getScoreReasonsFromScoreNotes(scoreNotesRaw: unknown): string | null {
  const o = parseScoreNotesRaw(scoreNotesRaw)
  return pickScoreNoteString(o, [
    "Score Reasons",
    "Score reasons",
    "score_reasons",
    "ScoreReasons",
  ])
}

export function hitDescriptionIsSqlError(desc: string | null | undefined): boolean {
  return Boolean(desc && desc.includes("SQLSTATE"))
}

/** Table cell for score column — `0` and `"0"` must not collapse to "—" (Angular `{{ score }}`). */
export function formatCreditHistoryScore(value: unknown): string {
  if (value === null || value === undefined) return "—"
  if (typeof value === "string" && value.trim() === "") return "—"
  return String(value)
}

/** Credit history from applicant first, then shell; JSON string or map. */
export function resolveCreditReportHistory(
  applicationData?: Record<string, unknown> | null,
  applicationAllData?: Record<string, unknown> | null,
): unknown[] {
  const raw =
    applicationData?.credit_report_history ??
    applicationAllData?.credit_report_history

  if (Array.isArray(raw)) return raw

  if (typeof raw === "string") {
    const t = raw.trim()
    if (!t) return []
    try {
      const parsed = JSON.parse(t) as unknown
      if (Array.isArray(parsed)) return parsed
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return Object.values(parsed as Record<string, unknown>)
      }
    } catch {
      return []
    }
    return []
  }

  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return Object.values(raw as Record<string, unknown>)
  }

  return []
}

export function creditHistoryRowKey(row: unknown, index: number): string | number {
  if (row && typeof row === "object" && !Array.isArray(row)) {
    const o = row as Record<string, unknown>
    const id = o.id ?? o.history_id ?? o.credit_report_history_id
    if (id !== undefined && id !== null && String(id) !== "") return String(id)
    const t = o.created_at
    if (t !== undefined && t !== null && String(t) !== "") return String(t)
  }
  return index
}

/** Angular `*ngIf` on applicant score block before score notes / history (line ~2133). */
export function hasEquifaxApplicantScoreBannerContext(
  applicationData?: Record<string, unknown> | null,
): boolean {
  if (!applicationData) return false
  if (applicationData.equifax_credit_report_path) return true
  const s = applicationData.equifax_score
  if (s === 0 || s === "0") return true
  if (s != null && s !== "") return true
  return false
}

/** Whether the Credit Report tab has bureau data to show (Angular never shows a global empty state). */
export function hasApplicantCreditReportData(
  applicationData?: CreditReportApplicationData | null,
): boolean {
  if (!applicationData) return false
  if (applicationData.credit_report === "1" || applicationData.credit_report === 1) {
    return true
  }
  if (isPresentScalar(applicationData.equifax_score)) return true
  if (isPresentScalar(applicationData.equifax_credit_report_path)) return true
  if (isPresentScalar(applicationData.corelogic_credit_report_path)) return true
  if (isPresentScalar(applicationData.corelogic_applicant_score)) return true
  return false
}

/** CoreLogic score block when path or score present (incl. `0`; not gated on active bureau). */
export function hasCoreLogicApplicantScoreData(
  applicationData?: CreditReportApplicationData | null,
): boolean {
  if (!applicationData) return false
  if (isPresentScalar(applicationData.corelogic_credit_report_path)) return true
  return isPresentScalar(applicationData.corelogic_applicant_score)
}

/** Angular Core-Logic h5 heading: config bureau CoreLogic or report path only. */
export function shouldShowCoreLogicSectionTitle(
  applicationData?: CreditReportApplicationData | null,
  configActive?: string | null,
): boolean {
  if (parseConfigCreditBureau(configActive) === "CoreLogic") return true
  return isPresentScalar(applicationData?.corelogic_credit_report_path)
}

/** Whether `CreditReportCoreLogicSection` should render (heading and/or score block). */
export function shouldShowCoreLogicSection(
  applicationData?: CreditReportApplicationData | null,
  configActive?: string | null,
): boolean {
  return (
    shouldShowCoreLogicSectionTitle(applicationData, configActive) ||
    hasCoreLogicApplicantScoreData(applicationData)
  )
}

/** Hide the React-only empty placeholder when paid apps can still create a bureau report. */
export function shouldShowCreditReportEmptyState(
  applicationData?: CreditReportApplicationData | null,
): boolean {
  if (!applicationData) return true
  if (applicationData.payment_log_id) return false
  return !hasApplicantCreditReportData(applicationData)
}

/** Open Credit Report visibility per active bureau (Angular creditinfoView). */
export function shouldShowOpenCreditReportButton(
  applicationData?: CreditReportApplicationData | null,
  configActive?: string | null,
): boolean {
  if (!applicationData) return false

  const bureau = resolveActiveCreditBureau({ configActive, applicationData })

  if (bureau === "ZipReport") {
    const cr = applicationData.credit_report
    return cr === "1" || cr === 1
  }

  if (bureau === "CoreLogic") {
    return isPresentScalar(applicationData.corelogic_credit_report_path)
  }

  if (bureau === "Equifax") {
    if (isPresentScalar(applicationData.equifax_credit_report_path)) return true
    const es = applicationData.equifax_score
    if (es === 0 || es === "0") return true
    if (isPresentScalar(es)) return true
    return isPresentScalar(applicationData.payment_log_id)
  }

  return false
}

/** ZipReport/CoreLogic Open is preview-only — disabled when the bureau path is missing. */
export function isOpenCreditReportDisabled(
  applicationData?: CreditReportApplicationData | null,
  configActive?: string | null,
): boolean {
  if (!applicationData) return true

  const bureau = resolveActiveCreditBureau({ configActive, applicationData })

  if (bureau === "ZipReport") {
    return !isPresentScalar(applicationData.credit_report_path)
  }

  if (bureau === "CoreLogic") {
    return !isPresentScalar(applicationData.corelogic_credit_report_path)
  }

  return false
}

export function resolveOpenCreditReportPath(
  applicationData?: CreditReportApplicationData | null,
  configActive?: string | null,
): string | null {
  if (!applicationData) return null

  const bureau = resolveActiveCreditBureau({ configActive, applicationData })

  if (bureau === "ZipReport") {
    const path = applicationData.credit_report_path
    return isPresentScalar(path) ? String(path) : null
  }

  if (bureau === "CoreLogic") {
    const path = applicationData.corelogic_credit_report_path
    return isPresentScalar(path) ? String(path) : null
  }

  if (bureau === "Equifax") {
    const path = applicationData.equifax_credit_report_path
    return isPresentScalar(path) ? String(path) : null
  }

  return null
}

/** Equifax `openCreditReport` may call regenerate when path is missing; Zip/CoreLogic preview only. */
export function openCreditReportMayRegenerate(
  applicationData?: CreditReportApplicationData | null,
  configActive?: string | null,
): boolean {
  const bureau = resolveActiveCreditBureau({ configActive, applicationData })
  if (bureau !== "Equifax") return false
  return resolveOpenCreditReportPath(applicationData, configActive) == null
}

/** Angular ZipReport tooltip when `credit_report_path` is missing. */
export function openCreditReportDisabledTooltip(
  applicationData?: CreditReportApplicationData | null,
  configActive?: string | null,
): string | undefined {
  if (!applicationData) return undefined
  const bureau = resolveActiveCreditBureau({ configActive, applicationData })
  if (bureau !== "ZipReport") return undefined
  if (isPresentScalar(applicationData.credit_report_path)) return undefined
  return "File Not Found"
}

/** One-line Equifax hit summary for the summary card (description + numeric hit code when both exist). */
export function formatHitSummaryLineFromScoreNotes(scoreNotesRaw: unknown): string {
  const code = getHitCodeFromScoreNotes(scoreNotesRaw)
  const desc = getHitDescriptionFromScoreNotes(scoreNotesRaw)
  if (desc && code) {
    if (desc === code) return desc
    const d = desc.trim()
    const c = code.trim()
    if (d.endsWith(`(${c})`) || d.includes(`(${c})`)) return d
    return `${d} (${c})`
  }
  if (desc) return desc
  if (code) return code
  return "—"
}

/** First non-empty value per key across sources (earlier wins). */
export function pickFirstNonEmptyByKey(
  ...sources: (Record<string, unknown> | null | undefined)[]
): Record<string, unknown> {
  const keys = new Set<string>()
  for (const s of sources) {
    if (!s || typeof s !== "object") continue
    for (const k of Object.keys(s)) keys.add(k)
  }
  const out: Record<string, unknown> = {}
  for (const k of keys) {
    for (const s of sources) {
      if (!s || typeof s !== "object") continue
      const v = s[k]
      if (v === null || v === undefined) continue
      if (typeof v === "string" && v.trim() === "") continue
      out[k] = v
      break
    }
  }
  return out
}

/** Unix seconds vs ms for `credit_report_history.created_at`. */
export function creditHistoryRowTimestamp(row: Record<string, unknown>): number {
  const c = row.created_at ?? row.request_time
  if (c == null) return 0
  if (typeof c === "number") return c < 1e12 ? c * 1000 : c
  const p = Date.parse(String(c))
  return Number.isFinite(p) ? p : 0
}

function normalizeJobIncomeToMonthly(income: number, period: string): number {
  const p = period.toLowerCase()
  if (p.includes("year") || p.includes("annual")) return income / 12
  if (p.includes("week")) return (income * 52) / 12
  if (p.includes("bi") && p.includes("week")) return (income * 26) / 12
  return income
}

/** Sum stated monthly income from `current_job` (Per_Month / year / week aware). */
export function sumApplicantCurrentJobMonthlyIncome(jobsRaw: unknown): number {
  if (!Array.isArray(jobsRaw)) return 0
  let t = 0
  for (const j of jobsRaw) {
    if (!j || typeof j !== "object" || Array.isArray(j)) continue
    const r = j as Record<string, unknown>
    const inc = Number(r.income)
    if (!Number.isFinite(inc) || inc <= 0) continue
    const period = String(r.income_period ?? "")
    t += normalizeJobIncomeToMonthly(inc, period)
  }
  return t
}

/** Monthly income fallback: jobs → verified_income → underwriter (≥12000 = annual). */
export function estimateApplicantMonthlyIncomeRecord(a: Record<string, unknown>): number {
  const job = sumApplicantCurrentJobMonthlyIncome(a.current_job)
  if (job > 0) return job
  const vi = Number(a.verified_income)
  if (Number.isFinite(vi) && vi > 0) return vi
  const uw = Number(a.underwriter_verified_income)
  if (!Number.isFinite(uw) || uw <= 0) return 0
  if (uw >= 12000) return uw / 12
  return uw
}

/** Sum `estimateApplicantMonthlyIncomeRecord` across `co_resident_application` map. */
export function sumHouseholdMonthlyIncomeFromApplication(
  applicationRoot: Record<string, unknown> | null | undefined,
): number {
  if (!applicationRoot) return 0
  const co = applicationRoot.co_resident_application
  if (!co || typeof co !== "object" || Array.isArray(co)) return 0
  let sum = 0
  for (const v of Object.values(co as Record<string, unknown>)) {
    if (v && typeof v === "object" && !Array.isArray(v)) {
      sum += estimateApplicantMonthlyIncomeRecord(v as Record<string, unknown>)
    }
  }
  return sum
}

export function pickLatestCreditReportHistoryRow(history: unknown[]): Record<string, unknown> | null {
  const rows = history.filter(
    (r) => r && typeof r === "object" && !Array.isArray(r),
  ) as Record<string, unknown>[]
  if (rows.length === 0) return null
  return rows.reduce((best, r) =>
    creditHistoryRowTimestamp(r) > creditHistoryRowTimestamp(best) ? r : best,
  )
}

function mergeScoreNotesFromHistoryRows(history: unknown[]): Record<string, unknown> {
  if (!Array.isArray(history) || history.length === 0) return {}
  const rows = history.filter(
    (r) => r && typeof r === "object" && !Array.isArray(r),
  ) as Record<string, unknown>[]
  if (rows.length === 0) return {}
  const sorted = [...rows].sort((a, b) => {
    const ta = creditHistoryRowTimestamp(a)
    const tb = creditHistoryRowTimestamp(b)
    if (ta !== tb) return ta - tb
    return 0
  })
  const acc: Record<string, unknown> = {}
  for (const r of sorted) {
    const sn = parseScoreNotesRaw(r.score_notes)
    for (const [k, v] of Object.entries(sn)) {
      if (v === null || v === undefined) continue
      if (typeof v === "string" && v.trim() === "") continue
      acc[k] = v
    }
  }
  return acc
}

/** Merge score notes: applicant → credit_report_data → history (newer row wins per key). */
export function mergeScoreNotesForSummary(
  applicationData?: Record<string, unknown> | null,
  creditReportData?: Record<string, unknown> | null,
  creditReportHistory?: unknown[] | null,
): Record<string, unknown> {
  const historyMerged = mergeScoreNotesFromHistoryRows(creditReportHistory ?? [])
  return pickFirstNonEmptyByKey(
    parseScoreNotesRaw(applicationData?.score_notes),
    parseScoreNotesRaw(creditReportData?.score_notes),
    historyMerged,
  )
}

/** Merge applicant + application-level `credit_report_data` (JSON string or object). Applicant wins. */
export function mergeCreditReportData(
  applicationData?: Record<string, unknown> | null,
  applicationAllData?: Record<string, unknown> | null,
): Record<string, unknown> {
  const shell = parseCreditReportDataField(applicationAllData?.credit_report_data)
  const applicant = parseCreditReportDataField(applicationData?.credit_report_data)
  return { ...shell, ...applicant }
}

export function parseCreditReportDataField(value: unknown): Record<string, unknown> {
  if (value == null || value === "") return {}
  if (typeof value === "string") {
    const t = value.trim()
    if (!t) return {}
    try {
      const parsed = JSON.parse(t) as unknown
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>
      }
    } catch {
      return {}
    }
    return {}
  }
  if (typeof value === "object" && !Array.isArray(value)) return value as Record<string, unknown>
  return {}
}

function pickNestedString(obj: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = obj[k]
    if (v != null && String(v).trim() !== "") return String(v)
  }
  return undefined
}

export function pickCreditReportDate(
  creditReportData: Record<string, unknown>,
  keys: string[],
): string | undefined {
  return pickNestedString(creditReportData, keys)
}

/** Angular `Applicant Score : {{ ... }}` — `0` / `"0"` → "No Score Available". */
export function formatEquifaxApplicantScoreBanner(
  applicationData?: Record<string, unknown> | null,
): string {
  const s = applicationData?.equifax_score
  if (s != null && s !== "" && s !== "0" && s !== 0) return String(s)
  return "No Score Available"
}

// ─── Collections / public records (credit_report_data JSON; multiple bureau key styles) ───

/** Coerce list fields that may be arrays or keyed maps (legacy PHP / bureau payloads). */
export function coerceCreditReportList(raw: unknown): unknown[] {
  if (raw == null) return []
  if (Array.isArray(raw)) return raw
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return Object.values(raw as Record<string, unknown>)
  }
  return []
}

function pickFirstCreditReportList(data: Record<string, unknown>, keys: string[]): unknown[] {
  for (const k of keys) {
    if (!Object.prototype.hasOwnProperty.call(data, k)) continue
    const arr = coerceCreditReportList(data[k])
    if (arr.length > 0) return arr
  }
  for (const k of keys) {
    if (!Object.prototype.hasOwnProperty.call(data, k)) continue
    return coerceCreditReportList(data[k])
  }
  return []
}

function pickRowStr(row: Record<string, unknown>, keys: string[], fallback = "—"): string {
  for (const k of keys) {
    const v = row[k]
    if (v != null && String(v).trim() !== "") return String(v).trim()
  }
  return fallback
}

function pickRowNum(row: Record<string, unknown>, keys: string[], fallback = 0): number {
  for (const k of keys) {
    const v = row[k]
    if (v == null || v === "") continue
    const n = Number(v)
    if (Number.isFinite(n)) return n
  }
  return fallback
}

function pickRowDateRaw(row: Record<string, unknown>, keys: string[]): string | number | null {
  for (const k of keys) {
    const v = row[k]
    if (v != null && String(v).trim() !== "") return v as string | number
  }
  return null
}


export function normalizeCreditCollectionRow(
  row: unknown,
  index: number,
): NormalizedCreditCollectionRow | null {
  if (!row || typeof row !== "object" || Array.isArray(row)) return null
  const r = row as Record<string, unknown>
  const creditor = pickRowStr(r, [
    "creditor",
    "Creditor",
    "creditor_name",
    "creditorName",
    "subscriber_name",
    "SubscriberName",
    "business_name",
    "BusinessName",
    "furnisher",
    "customer_name",
    "CustomerName",
  ])
  const original = pickRowStr(r, ["original_creditor", "OriginalCreditor"], "")
  const creditorDisplay =
    original && original !== "—" && creditor !== "—" && original !== creditor
      ? `${creditor} (original: ${original})`
      : creditor
  const accountNumber = pickRowStr(r, [
    "account_number",
    "accountNumber",
    "AccountNumber",
    "acct_number",
    "AcctNumber",
    "account",
    "Account",
  ])
  const amount = pickRowNum(r, [
    "amount",
    "Amount",
    "balance",
    "Balance",
    "current_balance",
    "CurrentBalance",
    "original_balance",
    "OriginalBalance",
    "past_due_amount",
    "PastDueAmount",
  ])
  const status = pickRowStr(r, [
    "status",
    "Status",
    "account_status",
    "AccountStatus",
    "narrative",
    "Narrative",
    "remarks",
    "Remarks",
  ])
  const dateOpenedRaw = pickRowDateRaw(r, [
    "date_opened",
    "dateOpened",
    "opened_date",
    "open_date",
    "DateOpened",
    "DATE_OPENED",
    "reported_date",
    "date_reported",
    "DateReported",
  ])
  const rowKey = `${accountNumber}|${creditor}|${String(amount)}|${index}`
  return {
    creditor: creditorDisplay,
    accountNumber,
    amount,
    status,
    dateOpenedRaw,
    rowKey,
  }
}

/** Prefer the first non-empty alias list on merged `credit_report_data`. */
export function extractNormalizedCollectionRows(
  data: Record<string, unknown>,
): NormalizedCreditCollectionRow[] {
  const raw = pickFirstCreditReportList(data, [
    "collections",
    "Collections",
    "collection_accounts",
    "CollectionAccounts",
    "collectionAccount",
    "collection",
  ])
  return raw
    .map((row, i) => normalizeCreditCollectionRow(row, i))
    .filter((x): x is NormalizedCreditCollectionRow => x != null)
}


export function normalizeCreditPublicRecordRow(
  row: unknown,
  index: number,
): NormalizedCreditPublicRecordRow | null {
  if (!row || typeof row !== "object" || Array.isArray(row)) return null
  const r = row as Record<string, unknown>
  const type = pickRowStr(r, [
    "type",
    "Type",
    "public_record_type",
    "record_type",
    "category",
    "bankruptcy_type",
    "BankruptcyType",
  ])
  const court = pickRowStr(r, ["court", "Court", "court_name", "CourtName", "filing_court"])
  const reference = pickRowStr(r, [
    "reference_number",
    "reference",
    "ReferenceNumber",
    "docket_number",
    "case_number",
    "filing_number",
    "FileNumber",
  ])
  const filedRaw = pickRowDateRaw(r, [
    "filed_date",
    "date_filed",
    "filed",
    "FileDate",
    "DATE_FILED",
    "disposition_date",
    "DispositionDate",
  ])
  const amount = pickRowNum(r, [
    "amount",
    "Amount",
    "balance",
    "Balance",
    "liability_amount",
    "claimed_amount",
  ])
  const status = pickRowStr(r, ["status", "Status", "disposition", "Disposition"])
  const rowKey = `${reference}|${type}|${index}`
  return { type, court, reference, filedRaw, amount, status, rowKey }
}

export function extractNormalizedPublicRecordRows(
  data: Record<string, unknown>,
): NormalizedCreditPublicRecordRow[] {
  const raw = pickFirstCreditReportList(data, [
    "public_records",
    "PublicRecords",
    "public_records_detail",
    "publicRecord",
    "PublicRecord",
  ])
  return raw
    .map((row, i) => normalizeCreditPublicRecordRow(row, i))
    .filter((x): x is NormalizedCreditPublicRecordRow => x != null)
}

export type { CreditReportApplicationData } from '../types/utils'

